import { ThreeDotMenu } from 'components/EpisodeEditorPublish/components/InteractivePoll/ThreeDotMenu';
import { SpotifyLogo } from 'components/svgs/SpotifyLogo';
import { useFetchPoll } from 'hooks/useInteractivityPolls';
import React, { useState } from 'react';
import { Modal } from '@spotify-internal/anchor-ds';
import { css } from 'emotion';
import { useT } from 'client/i18n/hooks/useT';
import { InteractivePollForm } from '../InteractivePollForm';
import {
  CopyContainer,
  OpenModalButton,
  Optional,
  RoundedCard,
  QuestionTitle,
  StatusText,
  Title,
  TopBarContainer,
} from './styles';
import { getPollStatusLabel } from './getPollStatusLabel';

export function InteractivePoll({
  episodeId,
  userId,
}: {
  episodeId: string;
  userId: number;
}) {
  const { data, status } = useFetchPoll(episodeId);
  const [isOpen, setIsOpen] = useState(false);
  const t = useT();

  if (!data || status === 'loading') return null;
  const { polls = [] } = data;
  const poll = polls[0] || {};

  return (
    <>
      <Title>
        {t(
          'EpisodeEditorPublish.InteractivePoll.Title',
          'Polls',
          'Interactive polls feature title'
        )}
      </Title>{' '}
      <Optional>
        {t(
          'EpisodeEditorPublish.InteractivePoll.TitleOnlyOnSpotify',
          '(Only available on Spotify)',
          'Interactive polls feature spotify exclusive'
        )}
      </Optional>
      <RoundedCard>
        <CopyContainer>
          <TopBarContainer>
            <SpotifyLogo width={63} />
            {poll.id && (
              <ThreeDotMenu
                episodeId={episodeId}
                userId={userId}
                status={poll.status}
              />
            )}
          </TopBarContainer>
          <QuestionTitle>
            {polls.length
              ? poll.question
              : t(
                  'EpisodeEditorPublish.InteractivePoll.PollTitle',
                  'Engage with listeners directly from your episode with Polls',
                  'Interactive polls feature title'
                )}
          </QuestionTitle>
          {poll.id && (
            <StatusText>
              {t(
                'EpisodeEditorPublish.InteractivePoll.PollStatus',
                'Status: {pollStatus}',
                'Interactive poll status label',
                {
                  pollStatus: getPollStatusLabel(poll.status, t),
                }
              )}
            </StatusText>
          )}
        </CopyContainer>
        <OpenModalButton
          kind="button"
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          color="purple"
        >
          {polls.length
            ? t('Button.EditPoll', 'Edit poll', 'Edit poll button text')
            : t('Button.AddPoll', 'Add a poll', 'Add a poll button text')}
        </OpenModalButton>
      </RoundedCard>
      {isOpen && (
        <Modal
          isShowing
          isShowingCloseButton
          aria-labelledby="interactive-poll-form-title"
          onClose={() => setIsOpen(false)}
        >
          <Modal.Title id="interactive-poll-form-title">
            {polls.length
              ? t(
                  'EpisodeEditorPublish.InteractivePoll.EditPollFormModalTitle',
                  'Edit poll',
                  'Edit poll form modal title'
                )
              : t(
                  'EpisodeEditorPublish.InteractivePoll.AddNewPollFormModalTitle',
                  'Add a new poll',
                  'Add a new poll form modal title'
                )}
          </Modal.Title>
          <Modal.Body
            className={css`
              width: 100%;
              text-align: left;
            `}
          >
            <InteractivePollForm
              onSuccess={() => setIsOpen(false)}
              poll={poll}
              episodeId={episodeId}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}
